// Pure helpers for useAdminMetrics — kept separate so unit tests don't pull
// in Firebase (whose Node ESM build needs polyfills jsdom doesn't provide).

export function dateNDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// Aggregate llm_usage_daily system docs by `field` ('by_function' | 'by_model').
// Returns { [key]: { calls, cost_usd } } summed across all docs.
export function aggregateBy(systemDocs, field) {
  const acc = {};
  for (const doc of systemDocs) {
    const breakdown = doc[field] || {};
    for (const [key, stats] of Object.entries(breakdown)) {
      if (!acc[key]) acc[key] = { calls: 0, cost_usd: 0 };
      acc[key].calls += stats.calls || 0;
      acc[key].cost_usd += stats.cost_usd || 0;
    }
  }
  return acc;
}

// Group per-user llm_usage_daily docs by uid; return top N by total_cost_usd.
// Excludes the "system" doc defensively.
export function topNByCost(perUserDocs, n = 10) {
  const acc = {};
  for (const doc of perUserDocs) {
    const uid = doc.uid;
    if (!uid || uid === 'system') continue;
    if (!acc[uid]) acc[uid] = { uid, calls: 0, cost_usd: 0 };
    acc[uid].calls += doc.total_calls || 0;
    acc[uid].cost_usd += doc.total_cost_usd || 0;
  }
  return Object.values(acc)
    .sort((a, b) => b.cost_usd - a.cost_usd)
    .slice(0, n);
}

// Group per-user llm_usage_daily docs by uid; return full map keyed by uid
// (no ranking, no limit). Used by the Users tab Usage column where every user
// needs a lookup, including those with zero activity in the window.
export function usageByUid(perUserDocs) {
  const acc = {};
  for (const doc of perUserDocs) {
    const uid = doc.uid;
    if (!uid || uid === 'system') continue;
    if (!acc[uid]) acc[uid] = { calls: 0, cost_usd: 0 };
    acc[uid].calls += doc.total_calls || 0;
    acc[uid].cost_usd += doc.total_cost_usd || 0;
  }
  return acc;
}

// Bucket Date objects (or Firestore Timestamp-likes with toDate) into per-UTC-day counts.
export function bucketByDay(dates) {
  const buckets = {};
  for (const d of dates) {
    const dt = d instanceof Date ? d : d.toDate();
    const day = dt.toISOString().slice(0, 10);
    buckets[day] = (buckets[day] || 0) + 1;
  }
  return Object.entries(buckets)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Pad a [{date, count}] series with zeros so the rolling window is contiguous.
export function fillMissingDays(series, days) {
  const byDate = Object.fromEntries(series.map((r) => [r.date, r.count]));
  const out = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key, count: byDate[key] || 0 });
  }
  return out;
}

// Resolve a Firebase users/{uid} doc to a display label.
// Preference order: "First Last" -> email -> uid prefix (10 chars) -> uid.
// Used by the LLMCostByUser tile so admins see human names instead of opaque uids.
export function formatUserName(userDoc, uid) {
  const fn = (userDoc?.first_name || '').trim();
  const ln = (userDoc?.last_name || '').trim();
  if (fn || ln) return `${fn} ${ln}`.trim();
  const email = (userDoc?.email || '').trim();
  if (email) return email;
  if (!uid) return '';
  return uid.length > 10 ? `${uid.slice(0, 10)}…` : uid;
}

// CSV serializer (RFC 4180 quoting). `columns` is array of strings or {key, header}.
export function toCSV(rows, columns) {
  const cols = columns.map((c) => (typeof c === 'string' ? { key: c, header: c } : c));
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [cols.map((c) => escape(c.header)).join(',')];
  for (const row of rows) {
    lines.push(cols.map((c) => escape(row[c.key])).join(','));
  }
  return lines.join('\n') + '\n';
}

// Trigger a browser download of a CSV string. No-op outside the browser.
export function downloadCSV(filename, csvString) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
